// The audit trail: what the server and the devices did, newest first, with
// the areas as chips, warnings and errors on their own, a search, and each
// line's attributes a tap away. Reads the server's table; nothing here is
// synced, and nothing here is edited.
import { useEffect, useState } from 'react'
import { AlertTriangle, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react'
import { api } from '@/runtime'
import type { AuditArea, AuditEvent } from '@/db/types'
import { Button, Failed, PageTitle, Segmented, Tag } from '@/components/ui'
import { Phrases } from '@/components/Phrases'
import { CodeBlock } from '@/components/Code'
import { useGrants } from '@/db/hooks'
import { AppTabs, HISTORY_TABS } from '@/components/AppTabs'

const AREA_LABEL: Record<string, string> = {
  voice: 'Voice',
  assistant: 'Assistant',
  spotify: 'Spotify',
  capture: 'Capture',
  memory: 'Memory',
  auth: 'Auth',
  push: 'Push',
  sync: 'Sync',
  device: 'Devices',
  system: 'System',
  library: 'Library',
  api: 'API tokens',
  factory: 'Factory',
}

/**
 * Who did it and to what, as names: a device's id is its name (the actor
 * already says it), a share link or member grant is its name, and the
 * server is the server. Nothing here shows `device:` or `grant:` ids.
 */
export const whoWords = (
  actor: string | undefined,
  subject: string | undefined,
  grantName: (id: string) => string | undefined,
): string[] => {
  const name = (s: string | undefined) => {
    if (!s) return ''
    if (s.startsWith('device:')) return s.slice(7) === actor ? '' : s.slice(7)
    if (s.startsWith('grant:')) return grantName(s.slice(6)) ?? 'a share link'
    return s === 'server' ? 'the server' : s
  }
  return [name(actor), name(subject)].filter((w, i, all) => w && all.indexOf(w) === i)
}

const fmtWhen = (iso: string) => {
  const d = new Date(iso)
  const today = new Date()
  const sameDay = d.toDateString() === today.toDateString()
  return sameDay
    ? d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : d.toLocaleString(undefined, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
}

export const ActivityView = () => {
  const [area, setArea] = useState<string>('')
  const [level, setLevel] = useState<'' | 'warn' | 'error'>('')
  const [q, setQ] = useState('')
  const [events, setEvents] = useState<AuditEvent[] | null>(null)
  const [areas, setAreas] = useState<AuditArea[]>([])
  const [open, setOpen] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)
  const [more, setMore] = useState(true)
  const grants = useGrants()
  const grantName = (id: string) => grants.find((g) => g.id === id)?.spec.name

  useEffect(() => {
    let stale = false
    api
      .auditAreas()
      .then((r) => {
        if (!stale) setAreas(r.areas)
      })
      .catch(() => {})
    return () => {
      stale = true
    }
  }, [tick])

  useEffect(() => {
    let stale = false
    const t = window.setTimeout(
      () => {
        api
          .audit({ area: area || undefined, level: level || undefined, q: q.trim() || undefined, limit: 100 })
          .then((r) => {
            if (stale) return
            setEvents(r.events)
            setMore(r.events.length >= 100)
            setError(null)
          })
          .catch((e: Error) => {
            if (!stale) setError(e.message)
          })
      },
      q ? 250 : 0,
    )
    return () => {
      stale = true
      window.clearTimeout(t)
    }
  }, [area, level, q, tick])

  // fresh while the page is looked at
  useEffect(() => {
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') setTick((n) => n + 1)
    }, 10_000)
    return () => window.clearInterval(t)
  }, [])

  const loadMore = async () => {
    const last = events?.[events.length - 1]
    if (!last) return
    const r = await api.audit({
      area: area || undefined,
      level: level || undefined,
      q: q.trim() || undefined,
      before: last.id,
      limit: 100,
    })
    setEvents((cur) => [...(cur ?? []), ...r.events])
    setMore(r.events.length >= 100)
  }

  return (
    <div className="flex flex-col gap-4" data-testid="activity">
      <div className="flex items-center justify-between gap-3">
        <PageTitle>History</PageTitle>
        <Button variant="chip" onClick={() => setTick((n) => n + 1)} aria-label="Refresh">
          <RefreshCw size={14} />
        </Button>
      </div>
      <div className="-mb-4">
        <AppTabs tabs={HISTORY_TABS} label="History" />
      </div>
      <p className="text-sm text-faint -mt-2">
        What the server and the devices did, newest first. Kept for two months.
      </p>
      {/* what to show (one pair), then where from */}
      <div data-testid="activity-level">
        <Segmented
          label="What to show"
          value={level === 'warn' ? 'warn' : ''}
          onChange={(v) => setLevel(v)}
          options={[
            { value: '' as const, label: 'Everything' },
            { value: 'warn' as const, label: 'Problems only' },
          ]}
        />
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Button variant={area === '' ? 'chipOn' : 'chip'} onClick={() => setArea('')}>
          All areas
        </Button>
        {areas.map((a) => (
          <Button key={a.area} variant={area === a.area ? 'chipOn' : 'chip'} onClick={() => setArea(a.area)}>
            {AREA_LABEL[a.area] ?? a.area}
          </Button>
        ))}
      </div>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search messages, actions, subjects…"
        className="w-full"
        data-testid="activity-search"
      />
      {error && <Failed what="the system's trail" reason={error} onRetry={() => setTick((n) => n + 1)} />}
      {events && events.length === 0 && <p className="text-sm text-faint">Nothing here yet.</p>}
      <ul className="flex flex-col divide-y divide-line rounded-[var(--radius-card)] border border-line overflow-hidden">
        {(events ?? []).map((e) => {
          const isOpen = open === e.id
          const hasAttrs = e.attrs && Object.keys(e.attrs).length > 0
          const who = whoWords(e.actor, e.subject, grantName)
          return (
            // a problem is an ordinary row with a named tag in the system's tone, never a tinted
            // band (yellow on beige could not be read)
            <li key={e.id} className="text-sm" data-testid="activity-row" data-level={e.level}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : e.id)}
                className="w-full flex items-start gap-3 px-3 py-2 text-left"
                aria-expanded={isOpen}
              >
                <span className="text-xs text-faint tabular-nums shrink-0 w-[4.6rem] pt-0.5">
                  {fmtWhen(e.at)}
                </span>
                <span className="text-xs text-muted shrink-0 w-16 pt-0.5 truncate">
                  {AREA_LABEL[e.area] ?? e.area}
                </span>
                <span className="min-w-0 flex-1">
                  {e.level !== 'info' && (
                    <span className="block mb-1">
                      <Tag tone={e.level === 'error' ? 'p3' : 'warn'}>
                        <AlertTriangle size={11} className="mr-1" aria-hidden />
                        {e.level === 'error' ? 'Error' : 'Warning'}
                      </Tag>
                    </span>
                  )}
                  <span className="block">
                    <span className="text-muted mr-2">{e.action.replace(/_/g, ' ')}</span>
                    {e.message && <span className="text-fg">{e.message}</span>}
                  </span>
                  {who.length > 0 && (
                    <Phrases as="span" className="flex text-xs text-faint truncate" parts={who} />
                  )}
                </span>
                {hasAttrs && (
                  <span className="shrink-0 text-faint pt-0.5">
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </span>
                )}
              </button>
              {isOpen && hasAttrs && (
                <CodeBlock
                  code={JSON.stringify(e.attrs, null, 2)}
                  lang="json"
                  className="mx-3 mb-3 text-xs text-fg-2"
                />
              )}
            </li>
          )
        })}
      </ul>
      {events && events.length > 0 && more && (
        <Button onClick={() => void loadMore()} className="self-center">
          Older
        </Button>
      )}
    </div>
  )
}
